import React, { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { withImageVersion } from "../lib/imageVersion";

/**
 * Lightbox — fullscreen overlay showing the angel artwork at full size.
 * Optional `onPrev` / `onNext` enable arrow-key + on-screen navigation.
 */
export default function Lightbox({ angel, onClose, onPrev, onNext, imageUrl, imageVersion }) {
    const handleKey = useCallback((e) => {
        if (e.key === "Escape") onClose?.();
        else if (e.key === "ArrowLeft") onPrev?.();
        else if (e.key === "ArrowRight") onNext?.();
    }, [onClose, onPrev, onNext]);

    useEffect(() => {
        document.addEventListener("keydown", handleKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = "";
        };
    }, [handleKey]);

    if (!angel) return null;
    const src = withImageVersion(
        imageUrl || angel.mainImageUrl,
        imageVersion ?? angel.imageVersion,
    );

    return (
        <div
            className="fixed inset-0 z-[100] bg-[#0c0b0a]/95 backdrop-blur-md flex items-center justify-center px-4 py-10"
            data-testid="lightbox-overlay"
            onClick={onClose}
        >
            <button
                type="button"
                aria-label="Close"
                onClick={(e) => { e.stopPropagation(); onClose?.(); }}
                data-testid="lightbox-close"
                className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center text-[#9a958a] hover:text-[#cfa052] border border-white/10 hover:border-[#cfa052]/40 rounded-full transition-colors"
            >
                <X className="w-5 h-5" />
            </button>

            {onPrev && (
                <button
                    type="button"
                    aria-label="Previous"
                    onClick={(e) => { e.stopPropagation(); onPrev(); }}
                    data-testid="lightbox-prev"
                    className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-[#9a958a] hover:text-[#cfa052] border border-white/10 hover:border-[#cfa052]/40 rounded-full transition-colors"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
            )}
            {onNext && (
                <button
                    type="button"
                    aria-label="Next"
                    onClick={(e) => { e.stopPropagation(); onNext(); }}
                    data-testid="lightbox-next"
                    className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-[#9a958a] hover:text-[#cfa052] border border-white/10 hover:border-[#cfa052]/40 rounded-full transition-colors"
                >
                    <ChevronRight className="w-6 h-6" />
                </button>
            )}

            <figure
                className="max-w-5xl w-full flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    src={src}
                    alt={`Angel ${angel.number}`}
                    className="max-h-[78vh] w-auto object-contain drop-shadow-[0_8px_60px_rgba(0,0,0,0.6)]"
                    data-testid="lightbox-image"
                />
                <figcaption className="mt-6 text-center">
                    <div className="font-label text-[10px] text-[#cfa052]">Angel № {angel.number}</div>
                    <div className="font-poetic text-[#9a958a] mt-2">{angel.statusText}</div>
                </figcaption>
            </figure>
        </div>
    );
}
